import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    debounceTime,
    exhaustMap,
    filter,
    forkJoin,
    map,
    of,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs";
import { SocketTransportService } from "../../../core/realtime/socket-transport.service";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";
import { SessionActions } from "../../authentication/store/session.actions";
import { sessionFeature } from "../../authentication/store/session.reducer";
import { UsersApiService } from "../data-access/users-api.service";
import { UsersCriteria } from "../models/user.models";
import { UsersActions, UsersPageDirection } from "./users.actions";
import { usersFeature } from "./users.reducer";

@Injectable()
export class UsersEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(UsersApiService);
    private readonly socket = inject(SocketTransportService);

    readonly initial$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.pageOpened),
            withLatestFrom(this.store.select(usersFeature.selectUsersState)),
            switchMap(([, state]) =>
                forkJoin({
                    counts: this.api.loadUserCounts(),
                    page: this.api.loadPage({ amount: state.pageSize }),
                }).pipe(
                    withSkeletonTiming(),
                    map(({ counts, page }) =>
                        UsersActions.pageLoaded({
                            ...page,
                            total: counts.registered,
                            invitedTotal: counts.preregistered,
                            requestCursor: "",
                            direction: "initial",
                            clientMode: false,
                        })
                    ),
                    catchError(() => of(UsersActions.pageLoadFailed()))
                )
            )
        )
    );

    readonly criteria$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.criteriaChanged),
            debounceTime(350),
            withLatestFrom(this.store.select(usersFeature.selectUsersState)),
            switchMap(([{ criteria }, state]) => {
                const clientMode = this.usesClientPagination(criteria);
                return this.api
                    .loadPage({
                        search: criteria.query,
                        amount: clientMode
                            ? Math.max(state.total, state.pageSize)
                            : state.pageSize,
                    })
                    .pipe(
                        map((page) =>
                            UsersActions.pageLoaded({
                                ...page,
                                total: state.total,
                                requestCursor: "",
                                direction: "initial",
                                clientMode,
                            })
                        ),
                        catchError(() => of(UsersActions.pageLoadFailed()))
                    );
            })
        )
    );

    readonly next$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.nextPageRequested),
            withLatestFrom(this.store.select(usersFeature.selectUsersState)),
            exhaustMap(([, state]) =>
                this.loadServerPage(
                    state.nextCursor ?? "",
                    "next",
                    state.total,
                    state.pageSize
                )
            )
        )
    );

    readonly previous$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.previousPageRequested),
            withLatestFrom(this.store.select(usersFeature.selectUsersState)),
            exhaustMap(([, state]) =>
                this.loadServerPage(
                    state.cursorHistory[Math.max(0, state.pageIndex - 1)] ?? "",
                    "previous",
                    state.total,
                    state.pageSize
                )
            )
        )
    );

    readonly update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.updateRequested),
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            switchMap(([{ userId, update }, currentUser]) =>
                this.api
                    .updateUser(
                        update.username,
                        update,
                        currentUser?.username !== update.username
                    )
                    .pipe(
                        map(() =>
                            UsersActions.updateSucceeded({ userId, update })
                        ),
                        catchError(() =>
                            of(UsersActions.updateFailed({ userId }))
                        )
                    )
            )
        )
    );

    readonly add$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.addRequested),
            switchMap(({ request }) =>
                this.api.addUser(request).pipe(
                    map(() =>
                        UsersActions.addSucceeded({ email: request.email })
                    ),
                    catchError(() =>
                        of(UsersActions.addFailed({ email: request.email }))
                    )
                )
            )
        )
    );

    readonly updateCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.updateSucceeded),
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            filter(
                ([{ update }, currentUser]) =>
                    Boolean(currentUser) &&
                    currentUser!.username === update.username
            ),
            map(([{ update }, currentUser]) =>
                SessionActions.currentUserUpdated({
                    user: {
                        ...currentUser!,
                        name: update.fullName,
                        roles: update.role,
                        profilePicture: update.profilePicture,
                    },
                })
            )
        )
    );

    readonly delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.deleteRequested),
            switchMap(({ userId, username }) =>
                this.api.deleteUser(username).pipe(
                    map(() =>
                        UsersActions.deleteSucceeded({ userId, username })
                    ),
                    catchError(() => of(UsersActions.deleteFailed({ userId })))
                )
            )
        )
    );

    readonly deletionSocket$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UsersActions.deleteSucceeded),
                tap(({ username }) => this.socket.emit("whoami", { username }))
            ),
        { dispatch: false }
    );

    readonly resetPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.passwordResetRequested),
            switchMap(({ userId, email }) =>
                this.api.requestPasswordReset(email).pipe(
                    map(() => UsersActions.passwordResetSucceeded({ userId })),
                    catchError(() =>
                        of(UsersActions.passwordResetFailed({ userId }))
                    )
                )
            )
        )
    );

    private loadServerPage(
        cursor: string,
        direction: UsersPageDirection,
        total: number,
        pageSize: number
    ) {
        return this.api.loadPage({ cursor, amount: pageSize }).pipe(
            map((page) =>
                UsersActions.pageLoaded({
                    ...page,
                    total,
                    requestCursor: cursor,
                    direction,
                    clientMode: false,
                })
            ),
            catchError(() => of(UsersActions.pageLoadFailed()))
        );
    }

    private usesClientPagination(criteria: UsersCriteria): boolean {
        return Boolean(
            criteria.query.trim() ||
            criteria.roles.length > 0 ||
            criteria.lite !== "any" ||
            criteria.individual !== "any"
        );
    }
}
